import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createSDK } from '@zdc/sdk';
import { launcherGetItem, launcherSetItem, launcherRemoveItem } from '@/lib/launcherStorage';
import { deviceLoginInfo } from '@/lib/deviceIdentity';

export interface Entity {
  id: string;
  login: string;
  name: string;
  email?: string;
  avatar_url?: string;
}

interface CenterState {
  token: string | null;
  entity: Entity | null;
  centerUrl: string;
  selectedDbaseId: string | null;
  /** slug выбранной базы (для входа в публикацию /enterprise/{slug}) */
  baseSlug: string | null;
  /** выбрана ОПУБЛИКОВАННАЯ база: web-клиент — только Предприятие (конфигуратор 403) */
  basePublished: boolean;

  setSelectedDbaseId: (id: string | null) => void;
  setCenterUrl: (url: string) => void;
  setBase: (slug: string | null, published: boolean) => void;
  login: (token: string, entity: Entity) => void;
  logout: () => void;
  loginWith1C: (employeeId: string, employeeName: string) => Promise<void>;
}

const CENTER_URL = '';

export const useCenterStore = create<CenterState>()(
  persist(
    (set, get) => ({
      token: null,
      entity: null,
      centerUrl: CENTER_URL,
      selectedDbaseId: null,
      baseSlug: null,
      basePublished: false,

      setSelectedDbaseId: (id) => set({ selectedDbaseId: id }),
      setCenterUrl: (url) => set({ centerUrl: url }),
      setBase: (slug, published) => set({ baseSlug: slug, basePublished: published }),

      login: (token, entity) => {
        set({ token, entity });
      },

      logout: () => {
        // baseSlug/basePublished НЕ сбрасываем — это данные выбранной базы
        // (сбрасываются явно в точках выхода: Header/PublicationShell)
        set({ token: null, entity: null });
      },

      loginWith1C: async (employeeId, employeeName) => {
        const url = get().centerUrl || window.location.origin;
        const sdk = createSDK({ baseUrl: url });
        const result = await sdk.auth.login({ login: employeeId, password: employeeId + '-hydra', ...deviceLoginInfo() });
        get().login(result.token || '', { id: result.entity?.id || employeeId, login: employeeId, name: employeeName });
      },


    }),
    {
      name: 'hydra-center-storage',
      // ЕДИНЫЙ слой данных лаунчера: на desktop запись идёт и в общий файл
      // (все окна видят один centerUrl/выбранную базу — «Новое окно» не теряет
      // список серверных баз). web/PWA — обычный localStorage.
      storage: createJSONStorage(() => ({
        getItem: launcherGetItem,
        setItem: launcherSetItem,
        removeItem: launcherRemoveItem,
      })),
      partialize: (state) => ({
        token: state.token,
        entity: state.entity,
        // centerUrl персистится (desktop): адрес ЦЕНТРА активной сессии. Без него
        // после перезапуска desktop-прокси указывает на ЛОКАЛЬНЫЙ сервер, а
        // запросы идут с X-Hydra-Base серверной базы → 404 «base not found» на
        // ВСЁ (лавина ретраев). Клиент Web/PWA: centerUrl == origin — безвредно.
        // Активная база ДОЛЖНА переживать F5 (базы-как-схемы): без неё все
        // запросы идут с пустым X-Hydra-Base → 400 «no base selected»
        centerUrl: state.centerUrl,
        baseSlug: state.baseSlug,
        basePublished: state.basePublished,
      }),
    }
  )
);
